/**
 * Ein einfaches I18N Framework für den Client, ein gleichnamiger Gegenpart existiert serverseitig für PHP.
 * Das Format der Sprachtexte ist JSON:
 *
 * <pre><code>
 * languageStore = {
 *    de: {
 *      __shortname: 'de',
 *      number_dec_point: ',',
 *      number_thousands_sep: '.',
 *      // hier die Sprachtexte als key-value mit optionalen Platzhaltern
 *      HelloXY: 'Hallo %s!',
 *      // weitere Keys
 *    }
 * }
 * </pre></code>
 *
 * Eine Übersetzung erhält man so:
 *
 * <pre><code>
 *     als globale Javascript-Funktion: I18N('HelloXY','Peter')
 *     als Handlebars-Helper: {{I18N "HelloXY" "Peter"}}
 * </pre></code>
 *
 * Der Languagestore sollte serverseitig beim Erzeugen des HTMLs als JSON ausgegeben werden und der Methode
 * I18N.setSingleLanguageStore(store) zugewiesen werden.
 *
 * __Tipp__: man kann alle I18N Texte eines Projektes mit folgendem node-Skript finden, das man im Basis-Ordner des Projektes aufruft:
 * [node holodeck/scripts/create_i18n_keys.js](https://github.com/OC-Git/Wirtschaftswunder/blob/master/holodeck/scripts/create_i18n_keys.js)
 */
/**
 * imports
 */
import sprintf from "sprintf-js";
import Urls from "oc-commons/Urls.js";
import Commons from "oc-commons/Commons.js";
import parseDecimalNumber from "parse-decimal-number";
const vsprintf = sprintf.vsprintf;

/**
 * Default-Store
 */
let languageStore = {
    de: {
        __shortname: 'de',
        number_dec_point: ',',
        number_thousands_sep: '.'
    },
    en: {
        __shortname: 'en',
        number_dec_point: '.',
        number_thousands_sep: ','

    }
};

let lang = 'de';
const missedAlreadyLogged = new Set();

/**
 * Gibt den in die aktuelle Sprache übersetzten Text zu einem Sprachkey zurück. Optional können weitere Parameter angegeben werden,
 * die mittels der sprintf Syntax im Sprachtext referenziert werden können.
 * @example
 * I18N(key,[parameter1],[parameter2],...) die optionalen Parameter werden an vsprintf übergeben,
 * so dass im Message-Value z.B. %s als Platzhalter verwendet werden kann.
 */
const I18N = function (...args) {
    if (_.isArray(args[0])) {
        args = args[0];
    }
    const key = args[0];
    if (Commons.isEmpty(key)) {
        return '';
    }
    const languageMap = languageStore[lang];
    const text = languageMap==null ? null : languageMap[key];
    if (text == null) {
        if (!missedAlreadyLogged.has(key)) {
            missedAlreadyLogged.add(key);
            console.warn(`lang=${lang} missing language key "${key}"`);
        }
//          für das Backend erstmal rausgenommen wegen Abwärtskompatibilität (sonst müssten alle Texte immer übersetzt werden...)
//         return 'I18N("' + key + '",' + args.slice(1).join(',') + ')';
//      das hier setzt vorraus dass der key dem Standard-Text entspricht, der unübersetzt ausgegeben würde, also mit Platzhalter.
//      das ist best practise - nimm einfach den originalen Text und mach' dir keine Gedanken über künstliche Keys...
        let label = key;
        if (args.length>1) {
            label += '│' + args.slice(1).join('│');
        }
        return '〖'+label+'〗';
    }
    try {
        return vsprintf(text, args.slice(1));
    } catch (e) {
        var errorText = 'error for I18N key "' + key + '" for language "' + lang + '": ' + e;
        console.warn(errorText, e);
        return errorText;
    }
};

/**
 * Gibt die KeyValue-Map für die aktuelle Sprache zurück
 */
I18N.getKeyValueMap = () => languageStore[lang];

/**
 * Setzt die KeyValue-Map für die Sprache entsprechend languageKeyValueMap.__shortname und setzt die Sprache auf languageKeyValueMap.__shortname
 */
I18N.setKeyValueMap = languageKeyValueMap => {
    if (languageKeyValueMap.__shortname) {
        lang = languageKeyValueMap.__shortname;
    }
    const store = {};
    store[lang] = languageKeyValueMap;
    I18N.setStore(store);
};


/**
 * Setzt den kompletten Sprachstore für alle Sprachen.
 * Defaults werden überschrieben, wenn im Store entsprechende Werte sind, ansonsten bleiben die Defaults bestehen.
 */
I18N.setStore = store => languageStore = Commons.defaultsDeepSafe({},store,languageStore);
I18N.slurpStore = requireContext => I18N.setStore(Commons.slurpDirectory(requireContext));

/**
 * Fügt Default-Werte für alle Sprachen hinzu. Bestehende Werte werden nicht überschrieben.
 */
I18N.setDefaults = store => Commons.defaultsDeepSafe(languageStore,store);
I18N.slurpDefaults = requireContext => I18N.setDefaults(Commons.slurpDirectory(requireContext));

/**
 * gibt den ISO2 - Code der aktuellen Sprache zurück, z.B. "de" oder "de-DE"
 */
I18N.getLanguage = function () {
    return lang;
};

/**
 * setzt den aktuellen ISO2-Code der Sprache, also z.B. "de" oder "de_DE"
 */
I18N.setLanguage = function (languageIso2Code) {
    if (!languageIso2Code) {
        console.warn('I18N.setLanguage: languageIso2Code may not be null or undefined!');
        return;
    }
    var languageMap = languageStore[languageIso2Code];
    if (languageMap) {
        if (!languageMap.__shortname) {
            languageMap.__shortname = languageIso2Code;
        }
        lang = languageMap.__shortname;
    } else {
        console.warn('I18N.setLanguage: there exists no language in store for isocode "' + languageIso2Code + '"');
    }
};

/**
 * erkennt die im Browser eingestellte Sprache
 */
I18N.autodetectLanguage = function() {
    var languageIso2Code = Urls.urlParam('lang');
    if (!languageIso2Code) {
        languageIso2Code = detectBrowserLanguage();
    }
    I18N.setLanguage(mapToKnownLanguage(languageIso2Code));
};

function detectBrowserLanguage() {
    var language = null;
    if (navigator && navigator.userAgent
        && (language = navigator.userAgent.match(/android.*\W(\w\w)-(\w\w)\W/i))) {
        language = language[1];
    }

    if (!language && navigator) {
        if (navigator.language) {
            language = navigator.language;
        } else if (navigator.browserLanguage) {
            language = navigator.browserLanguage;
        } else if (navigator.systemLanguage) {
            language = navigator.systemLanguage;
        } else if (navigator.userLanguage) {
            language = navigator.userLanguage;
        }
        language = language.substr(0, 2);
    }
    if (language == null) {
        language = lang;
    } else {
        var found = null;
        _.each(_.keys(languageStore), function (iso) {
            if (language.toLowerCase().indexOf(iso.toLowerCase()) == 0) {
                found = iso;
            }
        });
        language = (found == null) ? lang : found;
    }
    return language;
}

function mapToKnownLanguage(languageIso2Code) {
    if (languageIso2Code == null) {
        return lang;
    }
    if (languageIso2Code in languageStore) {
        return languageIso2Code;
    }
    languageIso2Code = languageIso2Code.split('[_- ]')[0];
    if (languageIso2Code in languageStore) {
        return languageIso2Code;
    }
    return lang;
}

/**
 * Ersetzt im übergebenen DOM-Element alle speziell ausgezeichneten Element-Inhalte bzw. Attribute.
 * @example
 * &lt;div data-i18n=&quot;key&quot;&gt;&lt;/div&gt; ersetzt den Inhalt des Elementes
 * &lt;div data-visibleForLanguage=&quot;de&quot;&gt;&lt;/div&gt; zeigt das Element nur an, wenn die Sprache "de" ist
 * &lt;input data-i18n-placeholder=&quot;key&quot;&gt; ersetzt Placeholder des Input-Feldes
 * &lt;input data-i18n-value=&quot;key&quot;&gt; ersetzt Value des Input-Feldes
 * &lt;div data-i18n-attribute=&apos;[{&quot;key&quot;:&quot;attribut&quot;,&quot;value&quot;:&quot;key&quot;},..]&apos;&gt; ersetzt attribut mit I18n(key)
 */
I18N.refreshElement = function(element) {
    $('*[data-i18n]', element).each(function () {
        var element = $(this);
        var key = element.data('i18n');
        var text = I18N(key);
        element.html(text);
    });
    $('*[data-visibleForLanguage]', element).each(function () {
        var element = $(this);
        var iso = element.attr('data-visibleForLanguage');
        if (iso == lang) {
            element.show();
        } else {
            element.hide();
        }
    });
    $('*[data-i18n-placeholder]', element).each(function () {
        var element = $(this);
        var key = element.attr('data-i18n-placeholder');
        var text = I18N(key);
        element.attr('placeholder', text);
    });
    $('*[data-i18n-value]', element).each(function () {
        var element = $(this);
        var key = element.attr('data-i18n-value');
        var text = I18N(key);
        element.attr('value', text);
    });
    $('*[data-i18n-attribute]', element).each(function () {
        var element = $(this);
        element.data('i18nAttribute').forEach((kvp)=>
        {
            element.attr(kvp.key,I18N(kvp.value));
        });
    });

};

/**
 * Prüft nach ob ein Key für die aktuelle Sprache existiert
 */
I18N.hasKey = function (key){
    const languageMap = languageStore[lang];
    if (languageMap==null) {
        return false;
    }
    return languageMap[key]!=null;
};

/**
 * formatiert eine Zahl im aktuellen lokalen Format
 * @param number
 * @param {number} [digits=2] optional, Anzahl der Nachkommastellen
 * @returns {string}
 */
I18N.formatFloat = function (number, digits=2) {
    if (typeof number !== 'number') {
        return '';
    }
    return number.toLocaleString(I18N.getLanguage(), {minimumFractionDigits: digits, maximumFractionDigits: digits});
};

/**
 * parsed einen Zahlstring im aktuellen lokalen Format
 * @param {((string|number))} numberString
 * @returns {number}
 */
I18N.parseFloat = function (numberString) {
    if (typeof numberString === 'number') {
        return numberString;
    }
    const map = I18N.getKeyValueMap();

    let options = (!_.isString(map.number_thousands_sep) || !_.isString(map.number_dec_point)) ? '.,' : map.number_thousands_sep+map.number_dec_point;

    return parseDecimalNumber(numberString, options);
};

/**
 * für einfachen globalen Zugriff auf das I18N-Objekt
 */
window['I18N'] = I18N;

export default I18N;
